import React, { useMemo } from 'react';
import Select, { StylesConfig, GroupBase } from 'react-select';

export type FilterOption = { value: string; label: string };

/** Dark theme aligned with App.css .form-select */
export const darkFilterSelectStyles: StylesConfig<FilterOption, false> = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    backgroundColor: '#2d2d2d',
    borderColor: '#444',
    boxShadow: 'none',
    '&:hover': { borderColor: '#6c757d' },
  }),
  singleValue: (base) => ({ ...base, color: '#fff' }),
  input: (base) => ({ ...base, color: '#fff' }),
  placeholder: (base) => ({ ...base, color: '#6c757d' }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#2d2d2d',
    border: '1px solid #444',
    zIndex: 1050,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, maxHeight: 280 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#0d6efd'
      : state.isFocused
        ? '#404040'
        : '#2d2d2d',
    color: '#fff',
    cursor: 'pointer',
  }),
  clearIndicator: (base) => ({ ...base, color: '#adb5bd', '&:hover': { color: '#fff' } }),
  dropdownIndicator: (base) => ({ ...base, color: '#adb5bd', '&:hover': { color: '#fff' } }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: '#444' }),
  noOptionsMessage: (base) => ({ ...base, color: '#adb5bd' }),
};

type Props = {
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  noOptionsMessage?: string;
};

const FilterSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  placeholder = 'Все',
  noOptionsMessage = 'Нет совпадений',
}) => {
  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  return (
    <Select<FilterOption, false, GroupBase<FilterOption>>
      options={options}
      value={selected}
      onChange={(option) => onChange(option?.value ?? '')}
      placeholder={placeholder}
      isSearchable
      isClearable
      noOptionsMessage={() => noOptionsMessage}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      captureMenuScroll={false}
      menuShouldBlockScroll
      menuShouldScrollIntoView={false}
      styles={darkFilterSelectStyles}
    />
  );
};

export default FilterSelect;
